import React, { useEffect, useState, useMemo } from "react";
import { SERVER_URL } from "../config";
import axios from "axios";
import TableHeaderFilter from "../components/TableHeaderfilter";
import SearchableDropdown from "../components/SearchableDropdown";
import { XMarkIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";

/* ─────────────────────────────────────────────────
   GOLD USER MODAL  –  Tabs: Gold | Call Register
───────────────────────────────────────────────── */
const GoldUserModal = ({ open, onClose }) => {
  const [activeTab, setActiveTab] = useState("gold"); // "gold" | "callRegister"

  /* ── Gold-user state ── */
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [businessOptions, setBusinessOptions] = useState([]);
  const [goldFilters, setGoldFilters] = useState({
    tallySlNo: [],
    customerName: [],
    contactPerson: [],
    userType: [],
    bussinessType: [],
    release: [],
    updatedBy: [],
  });

  /* ── Call-register state ── */
  const [callRecords, setCallRecords] = useState([]);
  const [selectedCall, setSelectedCall] = useState(null);
  const [callFilters, setCallFilters] = useState({
    companyName: [],
    tallySlNo: [],
    contactPerson: [],
    bussinessType: [],
    product: [],
    usertype: [],
  });

  /* ── Shared helpers ── */
  const fetchBusinessTypes = async () => {
    try {
      const res = await axios.get(`${SERVER_URL}/Rahul/getBusinessTypes.php`);
      if (res.data.status) {
        setBusinessOptions(res.data.data.map((b) => ({ label: b.name, value: b.name })));
      }
    } catch (err) { console.error(err); }
  };

  /* ── Gold user fetch ── */
  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${SERVER_URL}/Rahul/fetchGoldUserAndCustomerDetails.php`);
      if (res.data.status) setUsers(res.data.data);
    } catch (err) { console.error(err); }
  };

  /* ── Call-register fetch ── */
  // TODO: Replace with your actual API endpoint
  const fetchCallRecords = async () => {
    try {
      const res = await axios.get(`${SERVER_URL}/Rahul/fetchCallRegister.php`);
      if (res.data.status) setCallRecords(res.data.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    if (open) {
      fetchUsers();
      fetchBusinessTypes();
      fetchCallRecords();
    }
  }, [open]);

  /* ── Gold helpers ── */
  const goldTableColumns = [
    "slNo", "tallySlNo", "customerName", "contactPerson",
    "contactNumber", "expireDate", "updatedAt", "updatedBy",
  ];
  const goldFilterable = ["tallySlNo", "customerName", "contactPerson", "userType", "bussinessType", "release", "updatedBy"];
  const goldEditable = ["numberOfEmployees", "numberOfBranch", "numberOfTallyUser", "remark"];

  const getGoldOptions = (key) => {
    const unique = [...new Set(users.map((u) => u[key]).filter(Boolean))];
    return unique.map((val) => ({ label: val, value: val }));
  };

  const filteredUsers = useMemo(() => {
    return users.filter((u) =>
      Object.keys(goldFilters).every((key) =>
        goldFilters[key].length === 0 ? true : goldFilters[key].includes(u[key])
      )
    );
  }, [users, goldFilters]);

  const handleGoldChange = (key, value) => setSelectedUser((prev) => ({ ...prev, [key]: value }));

  const handleGoldUpdateSubmit = async () => {
    try {
      const employee = JSON.parse(localStorage.getItem("employee"));
      const payload = {
        ...selectedUser,
        updatedBy: `${employee?.employee_id} - ${employee?.employee_name}`,
      };
      // TODO: Replace with your actual API endpoint
      await axios.post(`${SERVER_URL}/Rahul/updateGoldUserAndCustomerDetails.php`, payload);
      fetchUsers();
      setSelectedUser(null);
    } catch (err) { console.error(err); }
  };

  /* ── Call-register helpers ── */
  const callTableColumns = [
    "callId", "companyName", "tallySlNo", "contactPerson", "usertype",
    "contactNumber", "alternateContactNumber", "bussinessType",
    "product", "gstNumber", "updatedBy", "updatedAt", "remarks",
  ];
  const callFilterable = ["companyName", "tallySlNo", "contactPerson", "bussinessType", "product", "usertype"];
  const callEditable = ["contactPerson", "bussinessType", "remarks"];

  const getCallOptions = (key) => {
    const unique = [...new Set(callRecords.map((r) => r[key]).filter(Boolean))];
    return unique.map((val) => ({ label: val, value: val }));
  };

  const filteredCalls = useMemo(() => {
    return callRecords.filter((r) =>
      Object.keys(callFilters).every((key) =>
        callFilters[key].length === 0 ? true : callFilters[key].includes(r[key])
      )
    );
  }, [callRecords, callFilters]);

  const handleCallChange = (key, value) => setSelectedCall((prev) => ({ ...prev, [key]: value }));

  const handleCallUpdateSubmit = async () => {
    try {
      const employee = JSON.parse(localStorage.getItem("employee"));
      const payload = {
        ...selectedCall,
        updatedBy: `${employee?.employee_id} - ${employee?.employee_name}`,
      };
      // TODO: Replace with your actual API endpoint
      await axios.post(`${SERVER_URL}/Rahul/updateCallRegister.php`, payload);
      fetchCallRecords();
      setSelectedCall(null);
    } catch (err) { console.error(err); }
  };

  const colLabel = (col) => {
    const map = {
      callId: "Call ID", companyName: "Company Name", tallySlNo: "Tally SL No.",
      contactPerson: "Contact Person", usertype: "User Type", contactNumber: "Contact No.",
      alternateContactNumber: "Alt. Contact No.", bussinessType: "Business Type",
      product: "Product", gstNumber: "GST Number", updatedBy: "Updated By",
      updatedAt: "Updated At", remarks: "Remarks", slNo: "SL No.",
      customerName: "Customer Name", expireDate: "Expire Date",
    };
    return map[col] || col;
  };

  if (!open) return null;

  /* ════════════ RENDER ════════════ */
  return (
    <>
      <style>{`
        .att-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
        .att-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
        .att-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .att-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>

      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center"
        style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(6px)" }}
      >
        <div className="w-[95%] max-w-[1400px] h-[90vh] rounded-2xl bg-white border border-slate-200 flex flex-col overflow-hidden">

          {/* ── HEADER ── */}
          <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center gap-3">
              {(selectedUser || selectedCall) && (
                <button
                  onClick={() => { setSelectedUser(null); setSelectedCall(null); }}
                  className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-200"
                >
                  <ArrowLeftIcon className="w-5 h-5" />
                </button>
              )}
              <h2 className="text-lg font-bold text-slate-800">
                {selectedUser
                  ? `Update Gold User — SL: ${selectedUser.slNo}`
                  : selectedCall
                    ? `Update Call Record — ID: ${selectedCall.callId}`
                    : "Gold Users"}
              </h2>
            </div>
            <button onClick={() => { setSelectedUser(null); setSelectedCall(null); onClose(); }}>
              <XMarkIcon className="w-6 h-6 text-slate-400" />
            </button>
          </div>

          {/* ── TABS (only when not in form view) ── */}
          {!selectedUser && !selectedCall && (
            <div className="flex-shrink-0 flex gap-1 px-6 pt-3 pb-0 border-b border-slate-200 bg-white">
              {[
                { key: "gold", label: "🪙 Gold Users" },
                { key: "callRegister", label: "📞 Call Register" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-5 py-2 text-sm font-semibold rounded-t-lg border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? "border-blue-600 text-blue-700 bg-blue-50"
                      : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {/* ══════════ GOLD TAB ══════════ */}
          {activeTab === "gold" && !selectedUser && (
            <div className="flex-1 overflow-auto att-scrollbar">
              <table className="min-w-full border-collapse">
                <thead className="sticky top-0 bg-slate-50">
                  <tr>
                    {goldTableColumns.map((col) => (
                      <th key={col} className="p-3 border text-left text-xs text-slate-500">
                        {goldFilterable.includes(col) ? (
                          <TableHeaderFilter
                            label={colLabel(col)}
                            options={getGoldOptions(col)}
                            selectedValues={goldFilters[col] || []}
                            onChange={(vals) => setGoldFilters((prev) => ({ ...prev, [col]: vals }))}
                          />
                        ) : (
                          <span className="font-semibold uppercase tracking-wider">{colLabel(col)}</span>
                        )}
                      </th>
                    ))}
                    <th className="p-3 border text-xs text-slate-500 font-semibold uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr key={u.slNo} className="border-t hover:bg-blue-50">
                      {goldTableColumns.map((key) => (
                        <td key={key} className="p-3 text-xs text-slate-600">
                          {key === "updatedAt" && u[key]
                            ? new Date(u[key]).toLocaleString("en-IN")
                            : u[key] ?? "-"}
                        </td>
                      ))}
                      <td className="p-3">
                        <button
                          onClick={() => setSelectedUser(u)}
                          className="px-3 py-1 text-xs text-white bg-green-500 rounded hover:bg-green-600"
                        >
                          Update
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr><td colSpan={goldTableColumns.length + 1} className="py-10 text-center text-slate-400 text-sm">No records found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Gold FORM ── */}
          {activeTab === "gold" && selectedUser && (
            <div className="flex-1 overflow-auto p-6 bg-slate-50 att-scrollbar">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {Object.keys(selectedUser)
                  .filter((key) => key !== "is_update")
                  .map((key) => {
                    if (key === "slNo") return null;
                    if (key === "bussinessType") {
                      return (
                        <div key={key}>
                          <label className="text-xs font-semibold text-slate-600 block mb-1">{colLabel(key)}</label>
                          <SearchableDropdown
                            options={businessOptions}
                            value={selectedUser[key]}
                            onChange={(val) => handleGoldChange(key, val)}
                          />
                        </div>
                      );
                    }
                    const isEditable = goldEditable.includes(key);
                    return (
                      <div key={key}>
                        <label className="text-xs font-semibold text-slate-600 block mb-1">{colLabel(key)}</label>
                        <input
                          value={selectedUser[key] || ""}
                          onChange={(e) => handleGoldChange(key, e.target.value)}
                          readOnly={!isEditable}
                          className={`w-full border p-2 rounded text-sm ${isEditable ? "bg-white border-slate-300 focus:ring-2 focus:ring-blue-400 focus:outline-none" : "bg-gray-100 text-slate-500"}`}
                        />
                      </div>
                    );
                  })}
              </div>
              <div className="mt-6 flex gap-3">
                <button onClick={handleGoldUpdateSubmit} className="px-5 py-2 bg-blue-600 text-white rounded text-sm font-semibold hover:bg-blue-700">Update</button>
                <button onClick={() => setSelectedUser(null)} className="px-5 py-2 border rounded text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
              </div>
            </div>
          )}

          {/* ══════════ CALL REGISTER TAB ══════════ */}
          {activeTab === "callRegister" && !selectedCall && (
            <div className="flex-1 overflow-auto att-scrollbar">
              <table className="min-w-full border-collapse">
                <thead className="sticky top-0 bg-slate-50">
                  <tr>
                    {callTableColumns.map((col) => (
                      <th key={col} className="p-3 border text-left text-xs text-slate-500">
                        {callFilterable.includes(col) ? (
                          <TableHeaderFilter
                            label={colLabel(col)}
                            options={getCallOptions(col)}
                            selectedValues={callFilters[col] || []}
                            onChange={(vals) => setCallFilters((prev) => ({ ...prev, [col]: vals }))}
                          />
                        ) : (
                          <span className="font-semibold uppercase tracking-wider">{colLabel(col)}</span>
                        )}
                      </th>
                    ))}
                    <th className="p-3 border text-xs text-slate-500 font-semibold uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCalls.map((r, idx) => (
                    <tr key={r.callId ?? idx} className="border-t hover:bg-blue-50">
                      {callTableColumns.map((key) => (
                        <td key={key} className="p-3 text-xs text-slate-600">
                          {key === "updatedAt" && r[key]
                            ? new Date(r[key]).toLocaleString("en-IN")
                            : r[key] ?? "-"}
                        </td>
                      ))}
                      <td className="p-3">
                        <button
                          onClick={() => setSelectedCall(r)}
                          className="px-3 py-1 text-xs text-white bg-green-500 rounded hover:bg-green-600"
                        >
                          Update
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filteredCalls.length === 0 && (
                    <tr><td colSpan={callTableColumns.length + 1} className="py-10 text-center text-slate-400 text-sm">No records found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Call Register FORM ── */}
          {activeTab === "callRegister" && selectedCall && (
            <div className="flex-1 overflow-auto p-6 bg-slate-50 att-scrollbar">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {callTableColumns.map((key) => {
                  if (key === "callId") return null;

                  if (key === "bussinessType" && callEditable.includes(key)) {
                    return (
                      <div key={key}>
                        <label className="text-xs font-semibold text-slate-600 block mb-1">{colLabel(key)}</label>
                        <SearchableDropdown
                          options={businessOptions}
                          value={selectedCall[key]}
                          onChange={(val) => handleCallChange(key, val)}
                        />
                      </div>
                    );
                  }

                  const isEditable = callEditable.includes(key);
                  return (
                    <div key={key}>
                      <label className="text-xs font-semibold text-slate-600 block mb-1">{colLabel(key)}</label>
                      <input
                        value={selectedCall[key] || ""}
                        onChange={(e) => handleCallChange(key, e.target.value)}
                        readOnly={!isEditable}
                        className={`w-full border p-2 rounded text-sm ${isEditable ? "bg-white border-slate-300 focus:ring-2 focus:ring-blue-400 focus:outline-none" : "bg-gray-100 text-slate-500"}`}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="mt-6 flex gap-3">
                <button onClick={handleCallUpdateSubmit} className="px-5 py-2 bg-blue-600 text-white rounded text-sm font-semibold hover:bg-blue-700">Update</button>
                <button onClick={() => setSelectedCall(null)} className="px-5 py-2 border rounded text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default GoldUserModal;